/* Use the storefront's existing SDK instances. Never initialize a second pixel or identity. */
(() => {
  const installed = Symbol.for('starry.amazonOutbound.installed');
  if (window[installed]) return;
  window[installed] = true;

  const eventName = 'AmazonOutboundClick';
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  const attributionKeys = ['maas', 'tag', 'ref_', 'ascsubtag'];

  function clickId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function ignoreRejection(result) {
    // A blocked SDK must never interrupt navigation or cause an unhandled rejection.
    result?.promise?.catch?.(() => {});
  }

  function trackActivation(event) {
    if (!event.isTrusted || event.defaultPrevented) return;
    if (event.type === 'click' && event.button !== 0) return;
    if (event.type === 'auxclick' && event.button !== 1) return;
    const link = event.target?.closest?.('a[data-amazon-outbound]');
    if (!link) return;

    try {
      const destination = new URL(link.href);
      // Malformed/unreviewed configuration is never sent to the analytics platforms.
      if (destination.protocol !== 'https:' || destination.username || destination.password || destination.port ||
          !['amazon.com', 'www.amazon.com'].includes(destination.hostname)) return;
      const asin = link.dataset.asin;
      if (!/^[A-Z0-9]{10}$/.test(asin || '')) return;
      const listing = destination.pathname.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:\/|$)/);
      if (!listing || listing[1] !== asin) return;

      const now = Date.now();
      const id = clickId();
      const source = new URL(window.location.href);
      const properties = {
        click_id: id,
        event_id: id,
        event_time_utc: new Date(now).toISOString(),
        product_id: link.dataset.productId,
        product_handle: link.dataset.productHandle,
        product_title: link.dataset.productTitle,
        variant_id: link.dataset.variantId,
        variant_sku: link.dataset.variantSku,
        asin,
        placement: link.dataset.placement,
        destination_url: link.href,
        page_path: source.pathname,
      };
      for (const key of campaignKeys) {
        if (source.searchParams.has(key)) properties[key] = source.searchParams.get(key);
      }
      const attribution = {};
      for (const key of attributionKeys) {
        if (destination.searchParams.has(key)) attribution[key] = destination.searchParams.get(key);
      }
      properties.amazon_attribution = attribution;
      if (document.referrer) {
        const referrer = new URL(document.referrer);
        properties.referrer = referrer.origin + referrer.pathname;
      }

      try {
        const amplitude = window.amplitude;
        // Use the existing plugin only after its SDK and session are ready.
        if (amplitude?.getDeviceId?.() && amplitude?.getSessionId?.() > 0 &&
            typeof amplitude.track === 'function' && typeof amplitude.flush === 'function') {
          // Amazon opens in another tab; preserve the plugin's transport for all storefront events.
          ignoreRejection(amplitude.track(eventName, properties, { insert_id: id, time: now }));
          ignoreRejection(amplitude.flush());
        }
      } catch {
        // Meta is independent of Amplitude availability.
      }

      try {
        const pixelId = link.dataset.metaPixelId;
        const fbq = window.fbq;
        if (/^\d+$/.test(pixelId || '') && typeof fbq === 'function' && typeof fbq.callMethod === 'function') {
          fbq('trackSingleCustom', pixelId, eventName, properties, { eventID: id });
        }
      } catch {
        // Native anchor activation always proceeds when analytics is unavailable.
      }
    } catch {
      // Invalid URLs, unavailable crypto, or unsupported webviews must not break the link.
    }
  }

  // Enter/touch generate click. Middle-click generates auxclick. No pointer/keydown duplicate.
  document.addEventListener('click', trackActivation);
  document.addEventListener('auxclick', trackActivation);
})();
